/**
 * Admin download helpers for administrative photo downloads
 */

import { photoService } from '../services/photo.service';
import { getDownloadStrategy, getDownloadDelay, detectDevice } from './deviceDetection';
import { Photo } from '../types';

export interface AdminDownloadProgress {
  completed: number;
  total: number;
  currentFile?: string;
  percentage: number;
}

export type AdminDownloadProgressCallback = (progress: AdminDownloadProgress) => void;

/**
 * Downloads a single photo for admin with proper filename
 */
async function downloadSinglePhotoAdmin(photo: Photo): Promise<void> {
  try {
    const blob = await photoService.downloadPhoto(photo.id);
    
    // Create a descriptive filename for admin downloads
    const sanitizedPhotoName = photo.originalName.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filename = `photo_${photo.id}_${sanitizedPhotoName}`;
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error(`Failed to download photo ${photo.originalName}:`, error);
    throw error;
  }
}

/**
 * Downloads multiple photos individually for admin with progress tracking
 */
async function downloadPhotosIndividuallyAdmin(
  photos: Photo[],
  onProgress?: AdminDownloadProgressCallback
): Promise<{ successful: number; failed: string[] }> {
  const successful: number[] = [];
  const failed: string[] = [];
  const delay = getDownloadDelay();
  
  for (let i = 0; i < photos.length; i++) {
    const photo = photos[i];
    
    // Update progress
    if (onProgress) {
      onProgress({
        completed: i,
        total: photos.length,
        currentFile: photo.originalName,
        percentage: Math.round((i / photos.length) * 100)
      });
    }
    
    try {
      await downloadSinglePhotoAdmin(photo);
      successful.push(photo.id);
      
      // Add delay between downloads to avoid overwhelming the browser
      if (i < photos.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`Failed to download ${photo.originalName}:`, error);
      failed.push(photo.originalName);
    }
  }
  
  // Final progress update
  if (onProgress) {
    onProgress({
      completed: photos.length,
      total: photos.length,
      percentage: 100
    });
  }
  
  return { successful: successful.length, failed };
}

/**
 * Downloads admin photos as a zip file (desktop strategy)
 */
async function downloadPhotosAsZipAdmin(photoIds: number[]): Promise<void> {
  const blob = await photoService.downloadMultiplePhotos(photoIds);
  
  const timestamp = new Date().toISOString().split('T')[0];
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `photos_admin_export_${timestamp}.zip`;
  a.style.display = 'none';
  
  document.body.appendChild(a);
  a.click();
  
  // Cleanup
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Smart download function for admin that chooses the best strategy based on device
 */
export async function downloadPhotosOptimizedAdmin(
  photos: Photo[],
  onProgress?: AdminDownloadProgressCallback
): Promise<{ successful: number; failed: string[]; strategy: string }> {
  const strategy = getDownloadStrategy();
  const device = detectDevice();
  
  console.log(`Admin download using strategy: ${strategy} for device:`, {
    isMobile: device.isMobile,
    isTablet: device.isTablet,
    isDesktop: device.isDesktop,
    screenWidth: device.screenSize.width
  });
  
  try {
    if (strategy === 'zip') {
      // Desktop zip download
      await downloadPhotosAsZipAdmin(photos.map(p => p.id));
      return { 
        successful: photos.length, 
        failed: [], 
        strategy: 'zip' 
      };
    } else {
      // Individual download for mobile/tablet
      const result = await downloadPhotosIndividuallyAdmin(photos, onProgress);
      return { 
        ...result, 
        strategy: strategy === 'individual' ? 'individual' : 'sequential' 
      };
    }
  } catch (error) {
    console.error('Admin download failed:', error);
    throw error;
  }
}

/**
 * Estimates download time for admin based on device and number of photos
 */
export function estimateAdminDownloadTime(photoCount: number): string {
  const device = detectDevice();
  const baseTimePerPhoto = device.isMobile ? 2 : device.isTablet ? 1.5 : 0.5; // seconds
  const delay = getDownloadDelay() / 1000; // convert to seconds
  
  const totalTime = (photoCount * baseTimePerPhoto) + ((photoCount - 1) * delay);
  
  if (totalTime < 60) {
    return `~${Math.ceil(totalTime)} secondes`;
  } else {
    const minutes = Math.ceil(totalTime / 60);
    return `~${minutes} minute${minutes > 1 ? 's' : ''}`;
  }
}

/**
 * Gets a user-friendly description of the admin download strategy
 */
export function getAdminDownloadStrategyDescription(): string {
  const strategy = getDownloadStrategy();
  
  switch (strategy) {
    case 'zip':
      return 'Export ZIP pour archivage (recommandé pour ordinateur)';
    case 'individual':
      return 'Téléchargement individuel des photos (optimisé pour mobile)';
    case 'sequential':
      return 'Téléchargement séquentiel (compatible avec tous les navigateurs)';
    default:
      return 'Export optimisé pour votre appareil';
  }
}